<script setup lang="ts">
import ProgressFrame from '@/modules/commissioning/views/layout/ProgressFrame.vue';
import { useCommissioningDeviceStore } from '@/modules/commissioning/stores/commissioningDeviceStore';
import type { ComponentErrorType } from '@/modules/commissioning/utils/error-constants';
import type { CommissionResult } from '@/modules/commissioning/models/CommissioningDevice';
import { getCommissionResultConfigValues } from '@/modules/commissioning/utils/error-resolver';
import ComErrorIds from '@/modules/commissioning/components/ComErrorIds.vue';
import ComErrorCtaButtons from '@/modules/commissioning/components/ComErrorCtaButtons.vue';
import { ERROR_CTA_BUTTONS } from '@/modules/commissioning/utils/error-constants';

const errorType = 'UnstableConditions' as ComponentErrorType;
const props = defineProps<{
  failIdsFromRoute?: string[]; // For debugging purposes only
}>();

const deviceStore = useCommissioningDeviceStore();
const commissionResult = deviceStore.commissionResultStatus.results ?? ({} as CommissionResult);

/**
 * Get the fail IDs from the commission result
 */
const commissionResultFailIds = getCommissionResultConfigValues(commissionResult, 'failId');
const failIds = props.failIdsFromRoute ?? commissionResultFailIds[errorType] ?? [];

/**
 * Get the CTA buttons from the commission result
 */
const commissionResultCtaButtons = getCommissionResultConfigValues(commissionResult, 'ctaButtons');
const ctaButtons = commissionResultCtaButtons[errorType] ?? [
  ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
  ERROR_CTA_BUTTONS.RECONFIGURE,
  ERROR_CTA_BUTTONS.TRY_AGAIN,
];
</script>

<template>
  <ProgressFrame
    pageClass="fullfocus-page"
    titleTight
    withBackArrow
    backButtonClass="error-back-button"
  >
    <template #title><div class="error-title">Unstable conditions</div></template>
    <template #subTitle>
      <div class="error-sub-title">
        Unstable conditions were detected during testing of the install environment.
      </div>
    </template>

    <div v-if="failIds.length > 0" class="error-fail-ids--wrapper">
      <ComErrorIds :failIds="failIds" unique />
    </div>

    <div class="error-intro">
      <p>
        The commissioning was not able to progress because of unstable conditions during testing the
        install environment. Please retry the commissioning again.
      </p>
    </div>

    <ComErrorCtaButtons :ctaButtons="ctaButtons" />
  </ProgressFrame>
</template>

<style lang="less" scoped></style>
